<script setup lang="ts">
const { trackEvent } = useGAnalytics();
const appStateStore = useAppStateStore();
const route = useRoute();
const routeName = computed(() => route.name);
const routePath = computed(() =>
  route.path.replace(/^\/[a-z]{2}(?=\/|$)/, "")
);

const marketRoutes = [
  {
    id: "store",
  },
  {
    id: "market",
  },
  {
    id: "collection",
  },
];

const getActiveMarketRoutes = computed(() => {
  const activePages = appStateStore.getMerchantActivePages(
    appStateStore.currentMerchant
  );
  if (!activePages) {
    return marketRoutes;
  }
  let filtered = marketRoutes.filter((route) => activePages.includes(route.id));
  if (!filtered.some((route) => route.id === "collection")) {
    const collectionRoute = marketRoutes.find(
      (route) => route.id === "collection"
    );
    if (collectionRoute) filtered.push(collectionRoute);
  }
  return filtered;
});

const userRoutes = [
  {
    id: "dashboard",
    name: "dashboard",
  },
  {
    id: "collection",
    name: "collection",
  },
  {
    id: "offers",
    name: "offers",
  },
  {
    id: "activity",
    name: "activity",
  },
  {
    id: "referrals",
    name: "referrals",
    requiresRole: "influencer",
  },
  {
    id: "account",
    name: "settings",
  },
];

const userStore = useUserStore();
const hasRole = (role: string) => userStore.roles?.includes(role) ?? false;
const builderWhitelist = computed(() =>
  String(
    useRuntimeConfig().public.whatsNewBuilderWhitelist || "christian@gfal.com",
  )
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean),
);
const canSeeWhatsNewBuilder = computed(() =>
  builderWhitelist.value.includes((userStore.email || "").toLowerCase()),
);
const displayInfluencerMenuForMarketing = useRuntimeConfig().public
  .allowInfluencerMenuForMarketing
  ? true
  : false;
const filteredUserRoutes = computed(() =>
  userRoutes.filter(
    (item) =>
      !item.requiresRole ||
      hasRole(item.requiresRole) ||
      (item.requiresRole === "influencer" && displayInfluencerMenuForMarketing)
  )
);

const scrollToElement = async (path: string) => {
  const combinedRoutes = [...marketRoutes, ...userRoutes];
  const route = combinedRoutes.find((route) => path.includes(route.id));
  if (!route) {
    return;
  }
  onNuxtReady(() => {
    setTimeout(() => {
      const el = document.getElementById(route.id);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "center",
        });
      }
    }, 50);
  });
};

watch(
  routeName,
  () => {
    if (route.path) {
      scrollToElement(route.path);
    }
  },
  { immediate: true }
);

const showAppMenu = ref(true);
const { y, directions } = useScroll(window, {
  behavior: "smooth",
});
const { top: toTop, bottom: toBottom } = toRefs(directions);

watch(y, () => {
  if (!showNav.value) {
    showAppMenu.value = true;
    return;
  }
  if (y.value < 150) {
    showAppMenu.value = true;
  } else {
    if (toBottom.value) {
      showAppMenu.value = false;
    } else if (toTop.value) {
      showAppMenu.value = true;
    }
  }
});
const isUserRoute = computed(() => false); //route.path.includes("user-portal"));
const isMarketRoute = computed(
  () =>
    routePath.value.includes("market") ||
    routePath.value.includes("store") ||
    routePath.value.includes("craft") ||
    (routePath.value.startsWith("/collection/") &&
      !routePath.value.startsWith("/user-portal/collection"))
);
const isPublicProfileRoute = computed(
  () => routePath.value.includes("user") || routePath.value.includes("collectible")
);
const showNav = computed(
  () => isUserRoute.value || isMarketRoute.value || isPublicProfileRoute.value
);

const isActiveLink = (linkName: string) => {
  return routePath.value.includes(linkName);
};
</script>
<template>
  <div :class="showNav && 'border-t border-secondary-500'"></div>
  <div
    v-if="showNav"
    class="px-4 sm:px-6 md:px-8 w-full overflow-y-hidden will-change-transform transition-all duration-300 ease-in-out font-bold text-neutral-300 text-base overflow-x-scroll lg:overflow-x-auto snap-x flex"
    :class="[
      showAppMenu ? 'h-16' : 'h-0',
      isUserRoute ? 'justify-normal md:justify-center' : 'justify-center',
    ]">
    <div v-if="isUserRoute" class="flex gap-8 md:gap-16">
      <NuxtLinkLocale
        v-for="route in filteredUserRoutes"
        :key="route.id"
        :id="route.id"
        class="min-w-fit pt-4 pb-5 transition-all duration-300 uppercase"
        :class="
            isActiveLink(route.id as string)
              ? 'text-neutral-200 border-b-4 border-neutral-200'
              : 'hover:text-neutral-200'
          "
        :to="`/user-portal/${route.id}`"
        :data-test="`link-nav-${route.id}`"
        @click="trackEvent(`navigation_${route.id}`)">
        {{ $t(`main.appMenu.${route.name}`) }}
      </NuxtLinkLocale>
    </div>
    <div v-else class="flex gap-8 md:gap-16">
      <NuxtLinkLocale
        v-for="route in getActiveMarketRoutes"
        :key="route.id"
        :id="route.id"
        class="min-w-fit pt-4 pb-5 transition-all duration-300 uppercase"
        :class="
            isActiveLink(route.id as string)
              ? 'text-neutral-200 border-b-4 border-neutral-200'
              : 'hover:text-neutral-200'
          "
        :to="`/${route.id}/${appStateStore.currentMerchant}`"
        :data-test="`link-nav-${route.id}-${appStateStore.currentMerchant}`"
        @click="trackEvent(`navigation_${route.id}`)">
        {{ $t(`main.appMenu.${route.id}`) }}
      </NuxtLinkLocale>
      <NuxtLinkLocale
        v-if="canSeeWhatsNewBuilder"
        id="whats-new-builder"
        class="min-w-fit mt-3 mb-3 px-3 rounded-lm transition-all duration-300 uppercase bg-[#3a2713] text-[#ffd7a0] border border-[#d6a366]/80 shadow-[0_0_0_1px_rgba(214,163,102,0.25)] hover:bg-[#5a3a1c] hover:text-[#ffe5c2]"
        to="/user-portal/whats-new-builder"
        data-test="link-nav-whats-new-builder"
        @click="trackEvent('navigation_whats_new_builder')">
        What's New Builder
      </NuxtLinkLocale>
    </div>
  </div>
  <div :class="showAppMenu ? 'border-b border-secondary-500' : null"></div>
</template>

<style scoped>
::-webkit-scrollbar {
  display: none;
}
</style>
